<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <ComSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <ComTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header & Search -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Staff Pictures</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage and update employee profile photos</p>
          </div>
          <div class="relative w-full sm:w-80">
            <input 
              v-model="searchQuery"
              type="text" 
              placeholder="Search by name, ID, or position..."
              class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent transition"
            >
            <div class="absolute left-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Section Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
          <nav class="-mb-px flex space-x-6 pb-1">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                activeTab === tab.id 
                  ? 'border-[#76ba51] text-[#76ba51] dark:text-[#76ba51]' 
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600', 
                'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- Staff Table -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff ID</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th scope="col" class="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Profile Photo</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="staff in filteredStaff" :key="staff.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500 dark:text-gray-400">#{{ staff.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-[#035a76] to-[#04698f] flex items-center justify-center text-white text-sm font-bold shadow-sm">
                        {{ getInitials(staff.full_name || staff.applicant_name) }}
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.full_name || staff.applicant_name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ staff.email || staff.applicant_email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.position_title || 'N/A' }}</div>
                    <div v-if="staff.section_name" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ staff.section_name }} <span v-if="staff.class_name">• {{ staff.class_name }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="relative group w-14 h-14 mx-auto">
                      <!-- Fallback to UI Avatars if no image is set -->
                      <img
                        :src="staff.image_file ? `${baseUrl}/${staff.image_file}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(staff.full_name || staff.applicant_name || 'User')}&background=035a76&color=fff&size=128`"
                        alt="Employee Profile"
                        class="w-14 h-14 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 group-hover:border-[#74bd54] transition-colors"
                      />
                      <!-- Hover Overlay for Upload -->
                      <div 
                        class="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        @click="triggerFileInput(staff.id)"
                        title="Change Photo"
                      >
                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <!-- Hidden File Input -->
                      <input
                        type="file"
                        accept="image/*"
                        :ref="el => setFileInputRef(el, staff.id)"
                        class="hidden"
                        @change="handleImageUpload($event, staff)"
                      />
                    </div>
                  </td>
                </tr>
                
                <!-- Empty State -->
                <tr v-if="filteredStaff.length === 0">
                  <td colspan="4" class="px-6 py-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No staff found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Staff Details Modal -->
        <StaffDetailsModal 
          v-if="selectedStaff"
          :staff="selectedStaff"
          @close="selectedStaff = null"
        />

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api';
import StaffDetailsModal from '../../components/hr/StaffDetailsModal.vue';
import ComSidebar from '../../components/com/ComSidebar.vue';
import ComTopbar from '../../components/com/ComTopbar.vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);
const currentPageTitle = ref('Staff Pictures');

const staffMembers = ref([]);
const fileInputs = ref({});
const activeTab = ref('all');
const searchQuery = ref('');
const selectedStaff = ref(null);

// Use VITE_API_URL or fallback to VITE_BASE_URL
const baseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_BASE_URL || '';

const tabs = [
  { id: 'all', name: 'All Staff' },
  { id: 'nursery', name: 'Nursery' },
  { id: 'primary', name: 'Primary' },
  { id: 'secondary', name: 'Secondary' },
  { id: 'outsource', name: 'Outsourced' },
  { id: 'non-teaching', name: 'Non-Teaching' },
  { id: 'extracurricular', name: 'Extracurricular' }
];

const fetchStaffMembers = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id,
      applicant_name: item.applicant_name,
      applicant_email: item.applicant_email,
      full_name: item.full_name,
      email: item.email,
      position_title: item.position?.position_title,
      section_name: item.section?.section_name,
      class_name: item.class?.class_name,
      joining_date: item.contract?.joining_date,
      status: item.applicant_status,
      image_file: item.contract?.employee_image,
      type: item.employee_type 
    }));
  } catch (error) {
    console.error('Error fetching staff members:', error);
  }
};

const filteredStaff = computed(() => {
  let filtered = staffMembers.value;
  
  // Filter by tab
  if (activeTab.value !== 'all') {
    filtered = filtered.filter(staff => {
      if (activeTab.value === 'nursery') return staff.section_name === 'Nursery';
      if (activeTab.value === 'primary') return staff.section_name === 'Primary';
      if (activeTab.value === 'secondary') return staff.section_name === 'Secondary';
      if (activeTab.value === 'outsource') return staff.type === 'outsource';
      if (activeTab.value === 'non-teaching') return staff.type === 'non-teaching';
      if (activeTab.value === 'extracurricular') return staff.type === 'extracurricular';
      return true;
    });
  }
  
  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(staff => {
      const name = (staff.full_name || staff.applicant_name || '').toLowerCase();
      const email = (staff.email || staff.applicant_email || '').toLowerCase();
      const position = (staff.position_title || '').toLowerCase();
      const id = String(staff.id).toLowerCase();

      return name.includes(query) || email.includes(query) || position.includes(query) || id.includes(query);
    });
  }
  
  return filtered;
});

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const setFileInputRef = (el, staffId) => {
  if (el) {
    fileInputs.value[staffId] = el;
  }
};

const triggerFileInput = (staffId) => {
  fileInputs.value[staffId]?.click();
};

const handleImageUpload = async (event, staff) => {
  const file = event.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append('applicant_id', staff.id);
  formData.append('image', file);

  try {
    const response = await api.post('upload_employee_image.php', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    console.log(response.data);
    // Refresh data after successful upload
    fetchStaffMembers();
  } catch (error) {
    console.error('Upload failed:', error);
  }
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

onMounted(() => {
  fetchStaffMembers();
});
</script>